import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectVideo } from '@/store/lms/selectors';

import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';

import { MediaPlayer, MediaProvider, Menu, Poster, usePlaybackRateOptions } from '@vidstack/react';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, OdometerIcon, SettingsIcon } from '@vidstack/react/icons';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';

import css from './index.module.scss';

export const MediaPlayerComp = props => {
  const { block, image, videoIDB, playerRef } = props;
  const { t } = useTranslation('video');

  const videoData = useSelector(selectVideo);

  const translationConst = {
    Audio: t('audio'),
    Boost: t('boost'),
    Loop: t('loop'),
    Mute: t('mute'),
    Normal: t('normal'),
    Pause: t('pause'),
    PiP: t('pip'),
    Play: t('play'),
    Playback: t('playback'),
    Settings: t('settings'),
    Speed: t('speed'),
    Unmute: t('unmute'),
    'Enter Fullscreen': t('enterFullscreen'),
    'Enter PiP': t('enterPiP'),
  };

  function SpeedSubmenu() {
    const options = usePlaybackRateOptions();
    const hint = options.selectedValue === '1' ? 'Normal' : options.selectedValue + 'x';

    return (
      <Menu.Root>
        <SubmenuButton label='Speed' hint={hint} disabled={options.disabled} icon={OdometerIcon} />
        <Menu.Content className='vds-menu-items'>
          <Menu.RadioGroup className='vds-radio-group' value={options.selectedValue}>
            {options.map(({ label, value, select }) => (
              <Menu.Radio className='vds-radio' value={value} onSelect={select} key={value}>
                <CheckIcon className='vds-icon' />
                <span className='vds-radio-label'>{label}</span>
              </Menu.Radio>
            ))}
          </Menu.RadioGroup>
        </Menu.Content>
      </Menu.Root>
    );
  }

  function SubmenuButton({ label, hint, icon: Icon, disabled }) {
    return (
      <Menu.Button className={css.SubmenuButton} disabled={disabled}>
        <ChevronLeftIcon className='vds-menu-close-icon' />
        <Icon className='vds-icon' />
        <span className='vds-menu-item-icon'>{label}</span>
        <span className='vds-menu-item-hint'>{hint}</span>
        <ChevronRightIcon className='vds-menu-open-icon' />
      </Menu.Button>
    );
  }

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const handlePlayError = () => player.play();

    const onPlay = () => {
      if (player) {
        try {
          player.play();
        } catch (error) {
          player.load();
          player.play();
        }
      }
    };

    player.addEventListener('play', onPlay);
    player.addEventListener('error', handlePlayError);

    return () => player.removeEventListener('error', handlePlayError);
  }, [playerRef]);

  return (
    <>
      <MediaPlayer
        ref={playerRef}
        title={block.name}
        src={{
          src: videoData.video && videoData.video.url ? videoData.video.url : videoIDB,
          type: 'video/mp4',
          crossOrigin: true,
        }}
        viewType='video'
        poster={image}
      >
        <MediaProvider>
          <Poster className='vds-poster' src={image} />
        </MediaProvider>
        <DefaultVideoLayout
          translations={translationConst}
          icons={defaultLayoutIcons}
          slots={{
            settingsMenu: (
              <Menu.Root className='vds-menu'>
                <Menu.Button className='vds-menu-button vds-button' aria-label='Settings'>
                  <SettingsIcon className='vds-rotate-icon vds-icon' />
                </Menu.Button>
                <Menu.Items className='vds-menu-items' placement='top' offset={0}>
                  <SpeedSubmenu />
                </Menu.Items>
              </Menu.Root>
            ),
          }}
        />
      </MediaPlayer>
    </>
  );
};
